import {
  CreateRuleRequestSchema,
  NOTIFICATION_CHANNELS,
  type NotificationChannel,
  RULE_CONDITION_TYPES,
  type RuleCondition,
  RuleConditionSchema,
  type RuleConditionType,
  type RuleDto,
  SEVERITY_NAMES,
  type Severity,
  THRESHOLD_OPERATORS,
  type ThresholdOperator,
} from '@vision360/contracts';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  CheckboxField,
  MetricFields,
  NumberField,
  SelectField,
  TextField,
} from '@/widgets/_sdk/fields';
import { formatDuration } from '@/widgets/_sdk/format';
import { useSources } from '@/widgets/_sdk/hooks';
import { useCreateRule, useUpdateRule } from './api';
import { BacktestRunner } from './backtest';

/**
 * Create or edit an alert rule (PRD VS-060). The draft keeps every type's
 * fields so switching type and back loses nothing; only the selected type is
 * sent. Durations are typed in minutes and sent in seconds.
 */

const FOR_PRESETS = [0, 60, 300, 900, 3600];
const SEVERITIES = [0, 1, 2, 3, 4] as const;

interface Draft {
  name: string;
  enabled: boolean;
  severity: Severity;
  channels: NotificationChannel[];
  cooldownMin: number | undefined;
  quietHoursOverride: boolean;
  type: RuleConditionType;
  sourceId: string;
  metricName: string;
  tags: Record<string, string>;
  operator: ThresholdOperator;
  value: number | undefined;
  forSeconds: number;
  kinds: string;
  minSeverity: Severity | undefined;
  titleContains: string;
  staleAfterMin: number | undefined;
  kind: string;
  withinMin: number | undefined;
}

const EMPTY: Draft = {
  name: '',
  enabled: true,
  severity: 2,
  channels: ['in-app'],
  cooldownMin: 5,
  quietHoursOverride: false,
  type: 'metric_threshold',
  sourceId: '',
  metricName: '',
  tags: {},
  operator: 'gt',
  value: undefined,
  forSeconds: 300,
  kinds: '',
  minSeverity: undefined,
  titleContains: '',
  staleAfterMin: 30,
  kind: '',
  withinMin: 60,
};

export function draftFromRule(rule: RuleDto | undefined): Draft {
  if (!rule) return EMPTY;
  const c = rule.condition;
  const base: Draft = {
    ...EMPTY,
    name: rule.name,
    enabled: rule.enabled,
    severity: rule.severity,
    channels: rule.channels,
    cooldownMin: rule.cooldownS / 60,
    quietHoursOverride: rule.quietHoursOverride,
    type: c.type,
  };
  switch (c.type) {
    case 'metric_threshold':
      return {
        ...base,
        sourceId: c.sourceId,
        metricName: c.metricName,
        tags: c.tags ?? {},
        operator: c.operator,
        value: c.value,
        forSeconds: c.forSeconds,
      };
    case 'event_match':
      return {
        ...base,
        sourceId: c.sourceId ?? '',
        kinds: c.kinds?.join(', ') ?? '',
        minSeverity: c.minSeverity,
        titleContains: c.titleContains ?? '',
      };
    case 'source_stale':
      return { ...base, sourceId: c.sourceId, staleAfterMin: c.staleAfterSeconds / 60 };
    case 'absence':
      return { ...base, sourceId: c.sourceId, kind: c.kind, withinMin: c.withinSeconds / 60 };
  }
}

const seconds = (minutes: number | undefined) =>
  minutes === undefined ? undefined : Math.round(minutes * 60);

/** The draft as the contract's condition; the schema judges it. */
function conditionOf(d: Draft): Record<string, unknown> {
  switch (d.type) {
    case 'metric_threshold':
      return {
        type: d.type,
        sourceId: d.sourceId,
        metricName: d.metricName,
        ...(Object.keys(d.tags).length > 0 ? { tags: d.tags } : {}),
        operator: d.operator,
        value: d.value,
        forSeconds: d.forSeconds,
      };
    case 'event_match': {
      const kinds = d.kinds
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      const title = d.titleContains.trim();
      return {
        type: d.type,
        ...(d.sourceId ? { sourceId: d.sourceId } : {}),
        ...(kinds.length > 0 ? { kinds } : {}),
        ...(d.minSeverity !== undefined ? { minSeverity: d.minSeverity } : {}),
        ...(title ? { titleContains: title } : {}),
      };
    }
    case 'source_stale':
      return { type: d.type, sourceId: d.sourceId, staleAfterSeconds: seconds(d.staleAfterMin) };
    case 'absence':
      return {
        type: d.type,
        sourceId: d.sourceId,
        kind: d.kind.trim(),
        withinSeconds: seconds(d.withinMin),
      };
  }
}

const requestOf = (d: Draft) => ({
  name: d.name.trim(),
  enabled: d.enabled,
  condition: conditionOf(d),
  severity: d.severity,
  channels: d.channels,
  cooldownS: seconds(d.cooldownMin),
  quietHoursOverride: d.quietHoursOverride,
});

const ERROR_FIELDS = [
  'name',
  'sourceId',
  'metricName',
  'value',
  'kinds',
  'titleContains',
  'staleAfterSeconds',
  'kind',
  'withinSeconds',
  'cooldownS',
  'channels',
] as const;
type ErrorField = (typeof ERROR_FIELDS)[number] | 'other';

/** First issue per field: `condition.value` → `value`. */
function fieldErrors(error: z.ZodError | undefined): Partial<Record<ErrorField, true>> {
  const out: Partial<Record<ErrorField, true>> = {};
  for (const issue of error?.issues ?? []) {
    const key = issue.path[0] === 'condition' ? issue.path[1] : issue.path[0];
    const field = ERROR_FIELDS.find((f) => f === key) ?? 'other';
    out[field] = true;
  }
  return out;
}

function SourcePicker({
  value,
  onChange,
  allowAll,
  error,
}: {
  value: string;
  onChange: (sourceId: string) => void;
  allowAll: boolean;
  error: string | undefined;
}) {
  const { t } = useTranslation('rules');
  const sources = useSources();
  const options = (sources.data?.sources ?? []).map((s) => ({ value: s.id, label: s.name }));
  return (
    <SelectField
      label={t('dialog.source')}
      value={value}
      placeholder={allowAll ? undefined : t('dialog.sourcePlaceholder')}
      options={allowAll ? [{ value: '', label: t('dialog.allSources') }, ...options] : options}
      onChange={onChange}
      error={error}
    />
  );
}

export function RuleDialog({ rule, onClose }: { rule?: RuleDto | undefined; onClose: () => void }) {
  const { t } = useTranslation(['rules', 'widgets']);
  const [draft, setDraft] = useState(() => draftFromRule(rule));
  const [submitted, setSubmitted] = useState(false);
  const create = useCreateRule();
  const update = useUpdateRule();
  const saving = rule ? update : create;

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const parsed = CreateRuleRequestSchema.safeParse(requestOf(draft));
  const errors = submitted ? fieldErrors(parsed.error) : {};
  const err = (field: ErrorField) => (errors[field] ? t(`rules:errors.${field}`) : undefined);

  const severityOptions = SEVERITIES.map((s) => ({
    value: String(s),
    label: t(`widgets:severity.${SEVERITY_NAMES[s]}`),
  }));
  const forOptions = [...new Set([...FOR_PRESETS, draft.forSeconds])]
    .sort((a, b) => a - b)
    .map((s) => ({
      value: String(s),
      label: s === 0 ? t('rules:dialog.forNow') : formatDuration(s),
    }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!parsed.success) return;
    if (rule) update.mutate({ id: rule.id, patch: parsed.data }, { onSuccess: onClose });
    else create.mutate(parsed.data, { onSuccess: onClose });
  };

  const testedCondition = (): RuleCondition | null => {
    const result = RuleConditionSchema.safeParse(conditionOf(draft));
    if (!result.success) setSubmitted(true);
    return result.success ? result.data : null;
  };

  const sourceError = err('sourceId');
  const toggleChannel = (channel: NotificationChannel, on: boolean) =>
    set({
      channels: on
        ? NOTIFICATION_CHANNELS.filter((c) => c === channel || draft.channels.includes(c))
        : draft.channels.filter((c) => c !== channel),
    });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel={t('rules:dialog.close')} className="animate-enter max-w-2xl">
        <DialogTitle>
          {rule ? t('rules:dialog.titleEdit', { name: rule.name }) : t('rules:dialog.titleNew')}
        </DialogTitle>
        <DialogDescription>{t('rules:dialog.description')}</DialogDescription>
        <form className="space-y-4" noValidate onSubmit={submit}>
          <TextField
            label={t('rules:dialog.name')}
            value={draft.name}
            onChange={(name) => set({ name })}
            error={err('name')}
          />
          <SelectField
            label={t('rules:dialog.conditionType')}
            value={draft.type}
            options={RULE_CONDITION_TYPES.map((type) => ({
              value: type,
              label: t(`rules:conditionTypes.${type}`),
            }))}
            onChange={(type) => set({ type })}
          />

          {draft.type === 'metric_threshold' && (
            <>
              <SourcePicker
                value={draft.sourceId}
                allowAll={false}
                error={sourceError}
                onChange={(sourceId) => set({ sourceId, metricName: '', tags: {} })}
              />
              <MetricFields
                sourceId={draft.sourceId || undefined}
                name={draft.metricName || undefined}
                tags={draft.tags}
                onChange={({ name, tags }) => set({ metricName: name, tags })}
              />
              {err('metricName') && <p className="text-xs text-destructive">{err('metricName')}</p>}
              <div className="grid gap-3 sm:grid-cols-3">
                <SelectField
                  label={t('rules:dialog.operator')}
                  value={draft.operator}
                  options={THRESHOLD_OPERATORS.map((op) => ({
                    value: op,
                    label: t(`rules:operators.${op}`),
                  }))}
                  onChange={(operator) => set({ operator })}
                />
                <NumberField
                  label={t('rules:dialog.value')}
                  value={draft.value}
                  onChange={(value) => set({ value })}
                  error={err('value')}
                />
                <SelectField
                  label={t('rules:dialog.forSeconds')}
                  value={String(draft.forSeconds)}
                  options={forOptions}
                  onChange={(value) => set({ forSeconds: Number(value) })}
                />
              </div>
            </>
          )}

          {draft.type === 'event_match' && (
            <>
              <SourcePicker
                value={draft.sourceId}
                allowAll
                error={sourceError}
                onChange={(sourceId) => set({ sourceId })}
              />
              <TextField
                label={t('rules:dialog.kinds')}
                value={draft.kinds}
                placeholder={t('rules:dialog.kindsHint')}
                onChange={(kinds) => set({ kinds })}
                error={err('kinds')}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <SelectField
                  label={t('rules:dialog.minSeverity')}
                  value={draft.minSeverity === undefined ? '' : String(draft.minSeverity)}
                  options={[
                    { value: '', label: t('rules:dialog.anySeverity') },
                    ...severityOptions,
                  ]}
                  onChange={(value) =>
                    set({ minSeverity: value === '' ? undefined : (Number(value) as Severity) })
                  }
                />
                <TextField
                  label={t('rules:dialog.titleContains')}
                  value={draft.titleContains}
                  onChange={(titleContains) => set({ titleContains })}
                  error={err('titleContains')}
                />
              </div>
            </>
          )}

          {draft.type === 'source_stale' && (
            <>
              <SourcePicker
                value={draft.sourceId}
                allowAll={false}
                error={sourceError}
                onChange={(sourceId) => set({ sourceId })}
              />
              <NumberField
                label={t('rules:dialog.staleAfter')}
                value={draft.staleAfterMin}
                min={1}
                max={10_080}
                step={1}
                onChange={(staleAfterMin) => set({ staleAfterMin })}
                error={err('staleAfterSeconds')}
              />
            </>
          )}

          {draft.type === 'absence' && (
            <>
              <SourcePicker
                value={draft.sourceId}
                allowAll={false}
                error={sourceError}
                onChange={(sourceId) => set({ sourceId })}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <TextField
                  label={t('rules:dialog.kind')}
                  value={draft.kind}
                  onChange={(kind) => set({ kind })}
                  error={err('kind')}
                />
                <NumberField
                  label={t('rules:dialog.within')}
                  value={draft.withinMin}
                  min={1}
                  max={10_080}
                  step={1}
                  onChange={(withinMin) => set({ withinMin })}
                  error={err('withinSeconds')}
                />
              </div>
            </>
          )}

          <BacktestRunner condition={testedCondition} />

          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label={t('rules:dialog.severity')}
              value={String(draft.severity)}
              options={severityOptions}
              onChange={(value) => set({ severity: Number(value) as Severity })}
            />
            <NumberField
              label={t('rules:dialog.cooldown')}
              value={draft.cooldownMin}
              min={0}
              max={1440}
              step={1}
              onChange={(cooldownMin) => set({ cooldownMin })}
              error={err('cooldownS')}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('rules:dialog.channels')}</legend>
            {NOTIFICATION_CHANNELS.map((channel) => (
              <CheckboxField
                key={channel}
                label={t(`rules:channels.${channel}`)}
                checked={draft.channels.includes(channel)}
                onChange={(on) => toggleChannel(channel, on)}
              />
            ))}
            {err('channels') && <p className="text-xs text-destructive">{err('channels')}</p>}
          </fieldset>
          <div className="space-y-1">
            <CheckboxField
              label={t('rules:dialog.quietHoursOverride')}
              checked={draft.quietHoursOverride}
              onChange={(quietHoursOverride) => set({ quietHoursOverride })}
            />
            <p className="pl-6 text-xs text-muted-foreground">
              {t('rules:dialog.quietHoursOverrideHint')}
            </p>
          </div>
          <CheckboxField
            label={t('rules:dialog.enabled')}
            checked={draft.enabled}
            onChange={(enabled) => set({ enabled })}
          />

          <div aria-live="polite">
            {submitted && !parsed.success && (
              <p className="text-sm text-destructive">{t('rules:dialog.invalid')}</p>
            )}
            {saving.isError && (
              <Alert variant="destructive" role="alert">
                {t('rules:dialog.saveFailed')}
              </Alert>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="h-11" onClick={onClose}>
              {t('rules:dialog.cancel')}
            </Button>
            <Button type="submit" className="h-11" disabled={saving.isPending}>
              {saving.isPending && <Spinner />}
              {saving.isPending
                ? t('rules:dialog.saving')
                : rule
                  ? t('rules:dialog.save')
                  : t('rules:dialog.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
